import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { studio } from "@/mocks/data";
import { nameField, emailField } from "@/lib/validators";

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "Settings — Studio OS" },
      { name: "description", content: "Studio profile, packages, and preferences." },
    ],
  }),
  component: SettingsPage,
});

const schema = z.object({
  name: nameField(100),
  owner: nameField(100),
  email: emailField,
});
type Values = z.infer<typeof schema>;

function SettingsPage() {
  const {
    register, handleSubmit, reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: studio.name, owner: studio.ownerName, email: studio.ownerEmail },
  });

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const onSubmit = handleSubmit(async (v) => {
    // TODO: PATCH /api/studio
    await new Promise((r) => setTimeout(r, 300));
    toast.success("Settings saved", { description: v.name });
    reset(v);
  });

  return (
    <div className="mx-auto max-w-3xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Settings" description="Studio profile and preferences." />

      <form onSubmit={onSubmit} className="space-y-8">
        <section className="rounded-xl bg-card p-6 ring-1 ring-border">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Studio profile
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="studio">Studio name</Label>
              <Input id="studio" {...register("name")} aria-invalid={!!errors.name} />
              {errors.name && <p className="text-[11px] text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="owner">Owner</Label>
              <Input id="owner" {...register("owner")} aria-invalid={!!errors.owner} />
              {errors.owner && <p className="text-[11px] text-destructive">{errors.owner.message}</p>}
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="email">Contact email</Label>
              <Input id="email" type="email" {...register("email")} aria-invalid={!!errors.email} />
              {errors.email && <p className="text-[11px] text-destructive">{errors.email.message}</p>}
            </div>
          </div>
        </section>

        <section className="rounded-xl bg-card p-6 ring-1 ring-border">
          <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Subscription
          </h2>
          <div className="flex items-center justify-between rounded-lg bg-surface-muted/60 p-4 ring-1 ring-border">
            <div>
              <p className="font-medium">{studio.plan} plan</p>
              <p className="text-xs text-muted-foreground">All features unlocked.</p>
            </div>
            <Button type="button" size="sm" variant="outline">Manage</Button>
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => reset()} disabled={!isDirty}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isDirty || isSubmitting}>
            {isSubmitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
